/**
 * Admin — "Pending requests" section. Lists members who asked for access via the
 * "Request access" screen (GET /admin/requests). Each links to that user's editor
 * where the admin grants events; granting clears the request server-side, so we
 * just refetch. No email infra — requests live in the access table.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IconBellRinging, IconChevronRight } from '@tabler/icons-react';
import { adminListRequests } from '../../lib/apiClient.ts';
import { Spinner } from '../ui/Spinner.tsx';

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function PendingRequests() {
  const { data: requests, isLoading } = useQuery({
    queryKey: ['admin-requests'],
    queryFn: ({ signal }) => adminListRequests(signal),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6 text-accent">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  if (!requests || requests.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-2xl border border-amber-500/30 bg-amber-500/5 shadow-sm">
      <div className="flex items-center gap-2 border-b border-amber-500/20 px-4 py-3">
        <IconBellRinging className="h-5 w-5 text-amber-500" />
        <h2 className="text-sm font-semibold text-fg">
          Pending requests
          <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
            {requests.length}
          </span>
        </h2>
      </div>
      <ul className="divide-y divide-amber-500/15">
        {requests.map((r) => (
          <li key={r.email}>
            <Link
              to={`/admin/users/${encodeURIComponent(r.email)}`}
              className="flex items-center gap-3 px-4 py-3 transition duration-200 ease-standard hover:bg-amber-500/10"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-fg">{r.name || r.email}</div>
                <div className="truncate text-xs text-muted">{r.email}</div>
              </div>
              {r.requestedAt && (
                <span className="shrink-0 text-xs text-fg-2">{formatWhen(r.requestedAt)}</span>
              )}
              <span className="shrink-0 text-xs font-medium text-accent">Grant access</span>
              <IconChevronRight className="h-4 w-4 shrink-0 text-muted" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
